import { useState } from 'react';

const questions = [
  { id: 1, text: '¿Has apostado más dinero del que podías permitirte perder?' },
  { id: 2, text: '¿Has necesitado apostar cantidades cada vez mayores para sentir la misma emoción?' },
  { id: 3, text: '¿Has intentado recuperar pérdidas apostando más (chasing losses)?' },
  { id: 4, text: '¿Has mentido a familiares o amigos sobre cuánto apuestas o pierdes?' },
  { id: 5, text: '¿Las apuestas han afectado negativamente tu trabajo, estudios o relaciones?' },
  { id: 6, text: '¿Has pedido dinero prestado o vendido algo para poder apostar?' },
  { id: 7, text: '¿Sientes ansiedad, irritabilidad o inquietud cuando no puedes apostar?' },
  { id: 8, text: '¿Has intentado dejar de apostar sin conseguirlo?' },
  { id: 9, text: '¿Piensas en las apuestas con frecuencia durante el día?' },
  { id: 10, text: '¿Has apostado para escapar de problemas o sentimientos negativos?' },
];

type Answer = 'si' | 'no' | null;

export default function SelfTestSection() {
  const [answers, setAnswers] = useState<Record<number, Answer>>({});
  const [result, setResult] = useState<null | { score: number; level: string; color: string; desc: string; advice: string }>(null);

  const handleAnswer = (id: number, val: Answer) => {
    setAnswers((prev) => ({ ...prev, [id]: val }));
    setResult(null);
  };

  const handleSubmit = () => {
    const answered = Object.keys(answers).length;
    if (answered < questions.length) return;
    const score = Object.values(answers).filter((a) => a === 'si').length;

    let level: string;
    let color: string;
    let desc: string;
    let advice: string;

    if (score === 0) {
      level = 'Sin riesgo aparente';
      color = '#00e87a';
      desc = 'No muestras señales de juego problemático. Sigue apostando de forma responsable y con límites claros.';
      advice = 'Mantén un registro de tus apuestas y establece límites de tiempo y dinero para seguir así.';
    } else if (score <= 2) {
      level = 'Riesgo bajo';
      color = '#a3e635';
      desc = 'Hay algunas señales leves. Es un buen momento para revisar tus hábitos antes de que escalen.';
      advice = 'Establece límites de depósito en tus casas de apuestas y tómate descansos regulares.';
    } else if (score <= 4) {
      level = 'Riesgo moderado';
      color = '#f59e0b';
      desc = 'Muestras señales de juego problemático. Es importante actuar ahora antes de que afecte más áreas de tu vida.';
      advice = 'Considera hablar con alguien de confianza y contactar con FEJAR (900 200 225) para orientación gratuita.';
    } else if (score <= 7) {
      level = 'Riesgo alto';
      color = '#f97316';
      desc = 'Las apuestas están teniendo un impacto significativo en tu vida. Buscar ayuda profesional es el paso correcto.';
      advice = 'Llama a FEJAR (900 200 225) o visita jugadores-anonimos.es. No estás solo en esto.';
    } else {
      level = 'Riesgo muy alto';
      color = '#ef4444';
      desc = 'Las señales indican un problema serio de juego. Buscar ayuda profesional es urgente y necesario.';
      advice = 'Llama ahora al 900 200 225 (FEJAR). Es gratuito, confidencial y disponible 24 horas. También puedes solicitar autoexclusión en las casas de apuestas.';
    }

    setResult({ score, level, color, desc, advice });
  };

  const allAnswered = Object.keys(answers).length === questions.length;
  const progress = Math.round((Object.keys(answers).length / questions.length) * 100);

  return (
    <section id="test" className="py-20 bg-[#080a0f]">
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="flex items-start gap-4 mb-10">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#06b6d4]/10 border border-[#06b6d4]/20 flex-shrink-0">
            <i className="ri-questionnaire-line text-[#06b6d4] text-xl"></i>
          </div>
          <div>
            <p className="text-[#06b6d4] text-xs font-semibold uppercase tracking-widest mb-1">Autoevaluación</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
              Test de Juego Responsable
            </h2>
            <p className="text-[#a8b2c1] text-sm max-w-2xl">
              Responde con honestidad. Este test es completamente anónimo y no se guarda ningún dato. Basado en el cuestionario SOGS (South Oaks Gambling Screen).
            </p>
          </div>
        </div>

        <div className="max-w-3xl">
          {/* Progress */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#6b7280] text-xs">{Object.keys(answers).length} de {questions.length} respondidas</span>
            <span className="text-[#6b7280] text-xs">{progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-[#1e2028] rounded-full mb-8">
            <div
              className="h-full bg-[#06b6d4] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          {/* Questions */}
          <div className="space-y-3 mb-8">
            {questions.map((q, i) => (
              <div
                key={q.id}
                className={`p-4 rounded-xl border transition-all ${
                  answers[q.id] ? 'bg-[#0d0f14] border-[#2a2d3a]' : 'bg-[#0d0f14] border-[#1e2028]'
                }`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-[#2a2d3a] font-bold text-sm flex-shrink-0 mt-0.5" style={{ fontFamily: "'Syne', sans-serif" }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-[#a8b2c1] text-sm leading-relaxed">{q.text}</p>
                </div>
                <div className="flex gap-2 ml-7">
                  <button
                    onClick={() => handleAnswer(q.id, 'si')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      answers[q.id] === 'si'
                        ? 'bg-[#ef4444]/20 text-[#ef4444] border border-[#ef4444]/40'
                        : 'bg-white/5 text-[#6b7280] border border-[#1e2028] hover:border-[#2a2d3a] hover:text-white'
                    }`}
                  >
                    Sí
                  </button>
                  <button
                    onClick={() => handleAnswer(q.id, 'no')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      answers[q.id] === 'no'
                        ? 'bg-[#00e87a]/20 text-[#00e87a] border border-[#00e87a]/40'
                        : 'bg-white/5 text-[#6b7280] border border-[#1e2028] hover:border-[#2a2d3a] hover:text-white'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!allAnswered}
            className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              allAnswered
                ? 'bg-[#06b6d4] text-black hover:bg-[#0891b2]'
                : 'bg-[#1e2028] text-[#4b5563] cursor-not-allowed'
            }`}
          >
            {allAnswered ? 'Ver mi resultado' : `Responde todas las preguntas (${questions.length - Object.keys(answers).length} restantes)`}
          </button>

          {/* Result */}
          {result && (
            <div className="mt-6 p-6 bg-[#0d0f14] border rounded-xl" style={{ borderColor: `${result.color}40` }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${result.color}15` }}>
                  <span className="text-xl font-bold" style={{ color: result.color, fontFamily: "'Syne', sans-serif" }}>{result.score}</span>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: result.color }}>Resultado</p>
                  <h3 className="text-white font-bold text-lg">{result.level}</h3>
                </div>
              </div>
              <p className="text-[#a8b2c1] text-sm leading-relaxed mb-4">{result.desc}</p>
              <div className="p-3.5 rounded-lg" style={{ backgroundColor: `${result.color}10`, borderLeft: `3px solid ${result.color}` }}>
                <p className="text-xs font-semibold mb-1" style={{ color: result.color }}>Recomendación</p>
                <p className="text-[#a8b2c1] text-sm">{result.advice}</p>
              </div>
              {result.score >= 3 && (
                <a
                  href="tel:900200225"
                  className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#ef4444]/10 border border-[#ef4444]/30 text-[#ef4444] text-sm font-bold hover:bg-[#ef4444]/20 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-sm"></i>
                  </div>
                  Llamar a FEJAR ahora · 900 200 225
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
